import { TypeMuxSongFields, TypeMuxVideoFields } from '@/core/generated-types';
import {
  RefetchTokenSetFn,
  VaultItem,
} from '@/lib/api/playbackService/hooks/useTokenSetVault';

export interface TypeMuxVideoUpload {
  ratio: string;
  ready: boolean;
  assetId: string;
  version: number;
  duration: number;
  uploadId: string;
  audioOnly: boolean;
  created_at: number;
  max_stored_frame_rate: number;
  max_stored_resolution: string;
}

export interface TypeSignedMuxVideoUpload extends TypeMuxVideoUpload {
  signedPlaybackId: string;
}

export interface TypeUnsignedMuxVideoUpload extends TypeMuxVideoUpload {
  playbackId: string;
}

export interface TypeSignedMuxVideoAsset extends TypeMuxVideoFields {
  muxVideoUpload: TypeSignedMuxVideoUpload;
}

export interface TypeUnsignedMuxVideoAsset extends TypeMuxVideoFields {
  muxVideoUpload: TypeUnsignedMuxVideoUpload;
}

export interface CourseVideoProps {
  muxVideo: TypeSignedMuxVideoAsset;
  isSigned: boolean;
  title: string;
  seriesTitle: string;
  onVideoComplete: () => void;
  onVideoProgress: (timeInSeconds: number) => void;
  refetchTokens: RefetchTokenSetFn;
  playbackServiceVideoId?: string | undefined;
  startTime?: number | undefined;
  userUuid?: string | undefined;
  tokenSet?: VaultItem | null | undefined;
  shouldAutoplay?: boolean;
  setIsVideoPlaying: (bool: boolean) => void;
}

export interface CourseVideoPreviewProps {
  muxVideo: TypeUnsignedMuxVideoAsset;
  isSigned: boolean;
  title: string | undefined;
}

export interface TypeMuxAudioUpload {
  version: number;
  uploadId: string;
  assetId: string;
  ready: boolean;
  max_stored_resolution: string;
  duration: number;
  audioOnly: boolean;
  created_at: number;
}

export interface TypeSignedMuxAudioUpload extends TypeMuxAudioUpload {
  signedPlaybackId: string;
}

export interface TypeUnsignedMuxAudioUpload extends TypeMuxAudioUpload {
  playbackId: string;
}

export interface TypeSignedMuxAudioAsset extends TypeMuxSongFields {
  muxAudioUpload: TypeSignedMuxAudioUpload;
}

export interface TypeUnsignedMuxAudioAsset extends TypeMuxSongFields {
  muxAudioUpload: TypeUnsignedMuxAudioUpload;
}

/**
 * supports the following scenarios:
 * - if a video has been completed, we replay from beginning
 * - corrupted head value of playback history
 *
 * If the startTime parameter is greater than the video length, the onEnded event is fired and video is reloaded from the beginning.
 * we can prevent the misfire of the onEnded event by first validating the head doesn't exceed the video length.
 *
 * @param startTime - where we intend to load the video from
 * @param videoLength - duration of the mux video asset
 */
export function _getValidatedStartTime(
  startTime: number | undefined,
  videoLength: number,
) {
  if (!startTime) {
    return 0;
  }

  const endTime = Math.floor(videoLength);
  return startTime < endTime ? startTime : 0;
}
